import { useState, useEffect } from "react";
import { ArrowRight, Check } from "lucide-react";

// AI Assistant Demo content with chat-style conversation
export const AIAssistantDemo = ({ isHovering }: { isHovering: boolean }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [messageSent, setMessageSent] = useState(false);

  const userPrompt = 'Create a morning shift for John next Monday 9-5';
  const thinkingSteps = [
    'Analyzing request...',
    'Finding employee John...',
    'Checking Monday availability...',
    'Creating shift...'
  ];

  // Reset animation when hover ends
  useEffect(() => {
    if (!isHovering) {
      setCurrentStep(0);
      setCharIndex(0);
      setIsFinished(false);
      setMessageSent(false);
    }
  }, [isHovering]);

  // Typewriter effect for input field, then send message
  useEffect(() => {
    if (!isHovering || isFinished) return;

    // Step 0: Type in input field
    if (currentStep === 0 && !messageSent) {
      if (charIndex < userPrompt.length) {
        const timeout = setTimeout(() => {
          setCharIndex(prev => prev + 1);
        }, 20);
        return () => clearTimeout(timeout);
      } else {
        // Done typing, send message after pause
        const timeout = setTimeout(() => {
          setMessageSent(true);
          setCurrentStep(1);
          setCharIndex(0);
        }, 300);
        return () => clearTimeout(timeout);
      }
    }

    // Steps 1+: AI thinking responses
    if (messageSent && currentStep > 0) {
      const currentText = thinkingSteps[currentStep - 1];
      if (!currentText) {
        setIsFinished(true);
        return;
      }

      if (charIndex < currentText.length) {
        const timeout = setTimeout(() => {
          setCharIndex(prev => prev + 1);
        }, 20);
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => {
          if (currentStep >= thinkingSteps.length) {
            setIsFinished(true);
          } else {
            setCurrentStep(prev => prev + 1);
            setCharIndex(0);
          }
        }, 700);
        return () => clearTimeout(timeout);
      }
    }
  }, [isHovering, currentStep, charIndex, isFinished, messageSent, thinkingSteps.length, userPrompt.length]);

  const getCurrentThinkingText = () => {
    if (!messageSent || currentStep === 0 || isFinished) return '';
    const step = thinkingSteps[currentStep - 1];
    return step ? step.slice(0, charIndex) : '';
  };

  const showAICursor = isHovering && messageSent && currentStep > 0 && !isFinished && charIndex < (thinkingSteps[currentStep - 1]?.length || 0);

  return (
    <div className="mt-auto bg-background/50 rounded-lg border border-border/50 h-[100px] flex flex-col">
      {/* Messages area - fixed height with relative positioning */}
      <div className="flex-1 p-2 overflow-hidden relative">
        {/* Empty state */}
        <div className={`absolute inset-2 flex items-center justify-center transition-opacity duration-200 ${isHovering ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
          <p className="text-[10px] text-muted-foreground/40 italic">Hover to see AI in action</p>
        </div>

        {/* Chat content container */}
        <div className={`absolute inset-2 flex flex-col justify-end space-y-1.5 transition-opacity duration-200 ${isHovering ? 'opacity-100' : 'opacity-0'}`}>
          {/* User message - right aligned */}
          <div className={`flex justify-end transition-all duration-200 ${messageSent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
            <div className="border border-secondary text-foreground px-2 py-1 rounded-lg rounded-br-sm max-w-[85%]">
              <p className="text-[10px] leading-tight">{userPrompt}</p>
            </div>
          </div>

          {/* AI response - left aligned */}
          <div className={`flex justify-start transition-all duration-200 ${messageSent && currentStep > 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
            <div className="bg-muted px-2 py-1 rounded-lg rounded-bl-sm max-w-[85%]">
              {!isFinished ? (
                <p className="text-[10px] text-muted-foreground leading-tight">
                  {getCurrentThinkingText()}
                  {showAICursor && (
                    <span className="inline-block w-[1px] h-2.5 bg-muted-foreground ml-0.5 animate-pulse" />
                  )}
                </p>
              ) : (
                <div className="space-y-0.5">
                  <p className="text-[10px] text-secondary flex items-center gap-1 leading-tight">
                    <Check className="h-2.5 w-2.5 shrink-0" />
                    Shift created
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Input field */}
      <div className="border-t border-border/50 p-1.5 flex items-center gap-1.5 shrink-0">
        <div className="flex-1 bg-background/80 rounded px-2 py-1 h-[24px] flex items-center overflow-hidden">
          {isHovering && !messageSent ? (
            <p className="text-[10px] text-foreground truncate">
              {userPrompt.slice(0, charIndex)}
              <span className="inline-block w-[1px] h-2.5 bg-secondary ml-0.5 animate-pulse" />
            </p>
          ) : (
            <p className="text-[10px] text-muted-foreground/40">
              {messageSent ? '' : 'Type a message...'}
            </p>
          )}
        </div>
        <button className={`p-1 rounded transition-colors shrink-0 ${isHovering && charIndex === userPrompt.length && !messageSent ? 'bg-secondary text-secondary-foreground' : 'bg-muted text-muted-foreground'}`}>
          <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
};
